import React from "react";
import {
  Box,
  Container,
  Stack,
  Card,
  CardContent,
  Typography,
  Link,
  CardActionArea,
} from "@mui/material";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ModalWithLauncher from "../modals/ModalWithLauncher";
import VideoPlayer from "../details/VideoPlayer";
import DocThumb from "../../../images/doc_thumb.png";
import VideoPlaceholder from "../../../images/video_placeholder.png";
import googleAnalytics from "shared/analytics";

const LandingLearnMore = () => {
  const VideoCardContent = () => {
    return (
      <Card
        onClick={() => googleAnalytics.trackVideoThumbnailClick("Landing Page Demo")}
        sx={(theme) => ({
          background: "#FFFFFF 0% 0% no-repeat padding-box",
          boxShadow: "0px 3px 20px #00000029",
          border: "10px solid #FFFFFF80",
          borderRadius: "20px",
          display: "flex",
          position: "relative",
          zIndex: 10,
          [theme.breakpoints.up("sm")]: {
            minHeight: "500px",
          },
        })}
      >
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flex: "1 1 100%",
          }}
        >
          <Box
            sx={{
              background: "#f9f9f9",
              flex: "1 1 100%",
              minHeight: "400px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              "&:hover": {
                "& img": {
                  opacity: ".6",
                },
                "& svg": {
                  opacity: ".8",
                },
              },
              "& img": {
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "cover",
                opacity: ".5",
              },
            }}
          >
            <PlayCircleFilledIcon
              sx={(theme) => ({
                fontSize: "200px",
                color: theme.palette.secondary.main,
                marginBottom: theme.spacing(3),
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: "10",
                opacity: ".6",
              })}
            />
            <img src={VideoPlaceholder} className="media" data-object-fit="cover" alt="" />
          </Box>
        </CardContent>
      </Card>
    );
  };
  const ModalVideoPlayer = () => (
    <VideoPlayer video={{ id: "OSaWHiYE2L8", title: "" }} width={640} height={360} />
  );

  const videoCardElement = (
    <Box
      sx={(theme) => ({
        marginBottom: theme.spacing(4),
        [theme.breakpoints.up("md")]: {
          marginBottom: 0,
        },
      })}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          align="center"
          sx={(theme) => ({
            fontWeight: "bold",
            marginBottom: theme.spacing(4),
            fontSize: 18,
            lineHeight: "22px",
            [theme.breakpoints.up("sm")]: {
              maxWidth: "65%",
              marginLeft: "auto",
              marginRight: "auto",
              fontSize: 20,
              lineHeight: "26px",
            },
          })}
        >
          Watch a demo, and hear from payers and life science companies about how they&apos;re using
          ICER Analytics
        </Typography>
      </Box>
      <ModalWithLauncher
        i18nKey="previewVideo"
        Content={ModalVideoPlayer}
        Launcher={VideoCardContent}
      />
    </Box>
  );

  const trackDocClick = () => {
    googleAnalytics.trackDownload({
      fileName: "/docs/ICER.Analytics.Overview.pdf",
      location: "Landing page - Learn more",
    });
    googleAnalytics.trackDocument("ICER.Analytics.Overview");
  };

  const docCardElement = (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          align="center"
          sx={(theme) => ({
            fontWeight: "bold",
            marginBottom: theme.spacing(4),
            fontSize: 18,
            lineHeight: "22px",
            [theme.breakpoints.up("sm")]: {
              maxWidth: "65%",
              marginLeft: "auto",
              marginRight: "auto",
              fontSize: 20,
              lineHeight: "26px",
            },
          })}
        >
          Download the ICER Analytics Overview
        </Typography>
      </Box>
      <Link
        href={"/docs/ICER.Analytics.Overview.pdf"}
        target="_blank"
        onClick={() => trackDocClick()}
      >
        <Card
          sx={(theme) => ({
            background: "#FFFFFF 0% 0% no-repeat padding-box",
            boxShadow: "0px 3px 20px #00000029",
            border: "10px solid #FFFFFF80",
            borderRadius: "20px",
            display: "flex",
            position: "relative",
            zIndex: 10,
            [theme.breakpoints.up("sm")]: {
              minHeight: "500px",
            },
          })}
        >
          <CardActionArea>
            <CardContent
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: "1 1 100%",
              }}
            >
              <Box
                sx={{
                  background: "#f9f9f9",
                  flex: "1 1 100%",
                  minHeight: "400px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  "&:hover": {
                    "& img": {
                      opacity: ".6",
                    },
                    "& svg": {
                      opacity: ".8",
                    },
                  },
                  "& img": {
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "cover",
                    opacity: ".5",
                  },
                }}
              >
                <CloudDownloadIcon
                  sx={(theme) => ({
                    fontSize: "200px",
                    color: theme.palette.secondary.main,
                    marginBottom: theme.spacing(3),
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: "10",
                    opacity: ".6",
                  })}
                />
                <img src={DocThumb} data-object-fit="cover" alt="" />
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      </Link>
    </Box>
  );

  return (
    <Box id="learn-more" component="section">
      <Container>
        <Box
          sx={(theme) => ({
            maxWidth: "100vw",
            position: "relative",
            [theme.breakpoints.up("md")]: {
              "&::after": {
                top: "50%",
                left: "50%",
                width: "200vw",
                content: '""',
                display: "block",
                zIndex: 0,
                position: "absolute",
                transform: "translate(-50%, -50%)",
                background: theme.palette.secondary.main,
                minHeight: "200px",
                borderTopLeftRadius: "102% 95%",
                borderTopRightRadius: "100% 95%",
                borderBottomLeftRadius: "120% 95%",
                borderBottomRightRadius: "90% 95%",
              },
            },
          })}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <Box sx={{ flex: { md: 2 } }}>{videoCardElement}</Box>
            <Box sx={{ flex: { md: 1 } }}>{docCardElement}</Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingLearnMore;
